import { useState, useEffect } from 'react';
import '../styles/NestedFeed.css';
import { Check, ChevronLeft, ChevronRight, Minus, RefreshCw, X } from 'lucide-react';
import { useNavigate } from 'react-router';
import { set } from 'date-fns';
import Modal from './Modal';

export const formatLastModified = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return ["", "", "", "", "", ""];

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    try {
        const hasTimeZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString);
        const isDateTimeWithSpace = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString);
        const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
        const isIsoWithoutZone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(dateString);

        let normalized = dateString;
        if (!hasTimeZone) {
            if (isDateTimeWithSpace) {
                normalized = `${dateString.replace(' ', 'T')}Z`;
            } else if (isDateOnly) {
                normalized = `${dateString}T00:00:00Z`;
            } else if (isIsoWithoutZone) {
                normalized = `${dateString}Z`;
            }
        }

        let parsedDate = new Date(normalized);

        if (isNaN(parsedDate.getTime())) {
            parsedDate = new Date(`${dateString}T00:00:00Z`);
        }

        if (isNaN(parsedDate.getTime())) {
            return ["", "", "", "", "", ""];
        }

        const fallbackTimeZone = "America/Chicago";
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || fallbackTimeZone;
        let zonedTime;
        try {
            zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: userTimeZone }));
        } catch (e) {
            zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: fallbackTimeZone }));
        }

        const iso = parsedDate.toISOString();
        const longDate = `${months[zonedTime.getMonth()]} ${zonedTime.getDate()}, ${zonedTime.getFullYear()}`;
        const shortDate = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()}`;
        const shortFormatted = `${zonedTime.getDate()} ${months[zonedTime.getMonth()].slice(0, 3)} ${zonedTime.getFullYear()}`;
        const hours = zonedTime.getHours();
        const minutes = zonedTime.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hour12 = hours % 12 || 12;
        const minutePadded = String(minutes).padStart(2, '0');
        const timeFormatted = `${hour12}:${minutePadded} ${ampm}`;
        const dateTimeFormatted = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()} ${timeFormatted}`;

        return [longDate, iso, timeFormatted, shortDate, shortFormatted, dateTimeFormatted];
    } catch (e) {
        console.error("Invalid date:", dateString, e);
        return ["", "", "", "", "", ""];
    }
};

const formatContentWithMentions = (content, users) => {
    if (!content || !users || users.length === 0) return content;

    // Decoded activity text is still untrusted. Only create our own mention
    // elements; React must render every part of the stored text as text.
    return content.split(/(@[a-zA-Z0-9_]+)/g).map((part, index) => {
        const mentionedUser = part.startsWith('@') && users.find(u => u.user === part.slice(1));
        return mentionedUser ? <span className="tag-mention" key={index}>{part}</span> : part;
    });
};

const clearHtmlTags = (html) => {
    if (!html || typeof html !== 'string') return '';
    // Template contents are inert and never attached to the document. Decode
    // legacy note markup once without activating its elements or event handlers.
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content.textContent || '';
};

export const NestedFeed = ({ case_id, section_id }) => {
    const [feedEntries, setFeedEntries] = useState([]);
    const [addActivity, setAddActivity] = useState(false);
    const [fullFeed, setFullFeed] = useState([]);
    const [page, setPage] = useState(1);
    const [loadingFeed, setLoadingFeed] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [itemCount, setItemCount] = useState(0);
    const [selectedActivity, setSelectedActivity] = useState([]);
    const [originalSelection, setOriginalSelection] = useState([]);
    const [users, setUsers] = useState([]);
    const [showRefreshCheck, setShowRefreshCheck] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [hoveredActivityId, setHoveredActivityId] = useState(null);
    const navigate = useNavigate();

    const fetchFeedEntries = async () => {
        if (isRefreshing) return; // Prevent multiple simultaneous refreshes
        
        setIsRefreshing(true);
        setShowRefreshCheck(false);
        
        try {
            const response = await fetch(`https://api.casedb.co/activity_feed.php?case_id=${case_id}&section_id=${section_id}`, { credentials: 'include' });
            const data = await response.json();
            setFeedEntries(data.feed_updates || []);
            
            // Show success check mark
            setIsRefreshing(false);
            setShowRefreshCheck(true);
            setTimeout(() => setShowRefreshCheck(false), 1500);
        } catch (error) {
            console.error('Error fetching feed entries:', error);
            // On error, just stop the spinner without showing check mark
            setIsRefreshing(false);
        }
    };

    const fetchFeed = async ({ page: nextPage = 1, append = false } = {}) => {
        try {
            if (loadingFeed) return;
            setLoadingFeed(true);
            const response = await fetch(`https://api.casedb.co/activity_feed.php?case_id=${case_id}&page=${nextPage}&per_page=100&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            const nextFeed = Array.isArray(data.feed_updates) ? data.feed_updates : [];
            // Sort by creation_date (newest first)
            const sortedFeed = nextFeed.sort((a, b) => new Date(b.creation_date) - new Date(a.creation_date));
            setFullFeed((prev) => (append ? [...prev, ...sortedFeed] : sortedFeed));
            setPage(nextPage);
            setHasMore(Boolean(data.pagination?.has_more));
            setTotalPages(data.pagination?.total_pages || 0);
            setItemCount(data.pagination?.total || 0);
        } catch (error) {
            console.error("Error fetching activity feed:", error);
        } finally {
            setLoadingFeed(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/user.php?time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (response.ok) {
                const processedUsers = data.users.map(user => ({
                    ...user,
                    user: user.user || user.name.toLowerCase().replace(/\s+/g, '_')
                }));
                setUsers(processedUsers);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const setActivitySection = async () => {
        try {
            // Identify items that were removed (in original but not in current selection)
            const removedIds = originalSelection.filter(id => !selectedActivity.includes(id));
            
            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'set_section',
                    case_id,
                    section_id,
                    activity_ids: selectedActivity,
                    removed_ids: removedIds
                })
            });
            const data = await response.json();
            
            if (data.success) {
                // Refresh the feed entries to show newly linked activities
                await fetchFeedEntries();
                // Close the modal
                setAddActivity(false);
                // Clear selection
                setSelectedActivity([]);
                setOriginalSelection([]);
            } else {
                console.error("Error setting activity section:", data.message);
                alert(`Failed to link activities: ${data.message}`);
            }
        } catch (error) {
            console.error("Error setting activity section:", error);
            alert('Failed to link activities. Please try again.');
        }
    };

    const handleSelectActivity = (activityId) => {
        setSelectedActivity(prev => 
            prev.includes(activityId)
                ? prev.filter(id => id !== activityId)
                : [...prev, activityId]
        );
    }

    useEffect(() => {
        fetchFeedEntries();
        fetchUsers();
    }, [case_id, section_id]);

    useEffect(() => {
        if (addActivity) {
            // When modal opens, pre-populate selectedActivity with already-linked items
            const alreadyLinkedIds = feedEntries.map(entry => entry.id);
            setSelectedActivity(alreadyLinkedIds);
            setOriginalSelection(alreadyLinkedIds);
            fetchFeed({ page: 1 });
        }
    }, [addActivity]);

    return (
        <div className='nested-feed'>
            <div className='toc-header'>
                <h4 className='nested-feed-header'>Linked Activity</h4>
                <div className='nested-actions'>
                    <button className='action tertiary' onClick={() => setAddActivity(true)}>+ Add Activity</button>
                    <div className='reload small-text' onClick={fetchFeedEntries}>
                        {showRefreshCheck ? (
                            <Check size={14} />
                        ) : (
                            <RefreshCw size={14} className={isRefreshing ? 'spinner small' : undefined} />
                        )}
                        Refresh
                    </div>
                </div>
            </div>
            <div className='nested-feed-entries'>
                {feedEntries.length > 0 && feedEntries?.map((entry, index) => {
                    const cleanContent = clearHtmlTags(entry.content);
                    const contentWithMentions = formatContentWithMentions(cleanContent, users);
                    return (
                        <div className='nested-entry' key={index}>
                            <div className='nested-entry-header'>
                                <div className='nested-entry-info subtext'>
                                    <span className='nested-entry-user'>{entry.author}</span>
                                    <span className='nested-entry-time'>{formatLastModified(entry.creation_date)[5]}</span>
                                </div>
                                <span className='nested-entry-nav' onClick={() => navigate(`/case/${case_id}?section=1&key=${entry.id}`)}>
                                    <ChevronRight size={16} />
                                </span>
                            </div>
                            <div className='nested-entry-body'>
                                <h4 className='nested-entry-subject'>{entry.subject}</h4>
                                <span className='nested-entry-content small-text'>{contentWithMentions}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
            {addActivity && (
                <Modal
                    title="Link Activity to Section"
                    onClose={() => setAddActivity(false)}
                    header={
                        <div className='modal-header-actions'>
                            <button className='action alt' onClick={() => setAddActivity(false)}>Cancel</button>
                            <button className='action' onClick={setActivitySection}>Save</button>
                        </div>
                    }
                    footer={
                        <div className='modal-footer-actions' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <button 
                                className='action alt' 
                                onClick={() => fetchFeed({ page: page - 1, append: false })}
                                disabled={page <= 1 || loadingFeed}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                                <ChevronLeft size={16} />
                                Previous
                            </button>
                            <span className='subtext' style={{ fontSize: '12px' }}>
                                Page {page} {totalPages > 0 ? `of ${totalPages}` : ''} • {itemCount} total activities
                            </span>
                            <button 
                                className='action alt' 
                                onClick={() => fetchFeed({ page: page + 1, append: false })}
                                disabled={!hasMore || loadingFeed}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                                Next
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    }
                    wider
                >
                    <div className='modal-content-wrapper'>
                        <div className='add-feed-entry-wrapper'>
                            {fullFeed.length === 0 ? (
                                <p className='small-text'>No activity found.</p>
                            ) : fullFeed.map((activity) => (
                                <div 
                                    key={activity.id} 
                                    className={`add-feed-entry ${selectedActivity.includes(activity.id) ? 'selected' : ''}`} 
                                    onClick={() => handleSelectActivity(activity.id)}
                                >
                                    <div 
                                        className={`add-feed-entry-check ${selectedActivity.includes(activity.id) ? 'checked' : ''}`}
                                        onMouseEnter={() => setHoveredActivityId(activity.id)}
                                        onMouseLeave={() => setHoveredActivityId(null)}
                                    >
                                        {selectedActivity.includes(activity.id) || hoveredActivityId === activity.id ? <Check size={16} /> : <Minus size={16} />}
                                    </div>
                                    <div className='add-feed-entry-info subtext'>
                                        <span className='add-feed-entry-user'>{activity.author}</span>
                                        <span className='add-feed-entry-time'>{formatLastModified(activity.creation_date)[5]}</span>
                                    </div>
                                    <div className='add-feed-entry-body'>
                                        <span className='add-feed-entry-subject'>{activity.subject}</span>
                                        <span className='add-feed-entry-content'>{formatContentWithMentions(clearHtmlTags(activity.content), users)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}
